import { useRouter, useSearch } from "@tanstack/react-router";
import { graphql, type FragmentType, useFragment } from "@repo/graphql-types";
import { CheckCircle } from "@repo/icons";
import { UserQueryDocument } from "@repo/graphql-types/graphql";
import {
  PublicPhoneNumberFormComponent,
  PublicPhoneNumberFormTitle,
  PublicPhoneNumberFormDescription,
} from "@/components/public-phone-number-form";
import type { UpsertPhoneFormValues } from "@/components/public-phone-number-form";
import {
  useGraphQLMutationWithErrorHandler,
  useInvalidateQuery,
} from "@/hooks/use-graphql";
import {
  MessageDrawerActionButton,
  MessageDrawerActions,
  MessageDrawerBody,
  MessageDrawerRoot,
  MessageDrawerTitle,
} from "@/components/message-drawer";
import { trackEvent } from "@/lib/tracking";

const UpsertPublicPhoneMutation = graphql(/* GraphQL */ `
  mutation UpsertPublicPhoneMutation($telefone: String) {
    atualizarTelefonePublico(input: { telefone: $telefone }) {
      boolean
      errors {
        ... on ValidationError {
          __typename
        }
      }
    }
  }
`);

export const UpsertPublicPhoneFragment = graphql(/* GraphQL */ `
  fragment UpsertPublicPhoneFragment on query_root {
    usuariosProfissionais {
      telefonePublico
    }
  }
`);

interface UpsertPublicPhoneComponentProps {
  data: FragmentType<typeof UpsertPublicPhoneFragment>;
}

export const UpsertPublicPhoneComponent = ({
  data,
}: UpsertPublicPhoneComponentProps): JSX.Element => {
  const router = useRouter();
  const invalidateUserQuery = useInvalidateQuery(UserQueryDocument);
  const fragmentData = useFragment(UpsertPublicPhoneFragment, data);
  const defaultPhone = fragmentData.usuariosProfissionais[0]?.telefonePublico ?? "";

  const { mutateAsync, isPending } = useGraphQLMutationWithErrorHandler(
    UpsertPublicPhoneMutation,
  );

  const onSubmit = async (formData: UpsertPhoneFormValues): Promise<void> => {
    const formPhone = formData.phone;
    await mutateAsync(
      { telefone: formPhone },
      {
        onSuccess: () => {
          trackEvent("Telefone público informado", {
            numeroTelefone: formPhone,
            fluxo: "Perfil do membro",
          });
          
          invalidateUserQuery();
          void router.navigate({
            to: "/settings/profile/upsert-public-phone/",
            search: { showSuccessDrawer: true },
            replace: true,
          });
        },
      },
    );
  };

  return (
    <>
      <PublicPhoneNumberFormComponent
        defaultPhone={defaultPhone}
        onSubmit={onSubmit}
        loading={isPending}
        submitLabel="Confirmar telefone"
      >
        <PublicPhoneNumberFormTitle>
          Confirme ou atualize o telefone do seu consultório
        </PublicPhoneNumberFormTitle>
        <PublicPhoneNumberFormDescription>
          Esse é o número que será informado aos seus pacientes para entrarem em contato
          com você.
        </PublicPhoneNumberFormDescription>
      </PublicPhoneNumberFormComponent>
      <SuccessUpsertPublicPhoneDrawer />
    </>
  );
};

const SuccessUpsertPublicPhoneDrawer = (): JSX.Element => {
  const searchParams = useSearch({
    from: "/settings/profile/upsert-public-phone/",
  });
  const router = useRouter();
  return (
    <MessageDrawerRoot
      icon={CheckCircle}
      dismissible={false}
      setOpen={(open) => {
        if (!open) {
          void router.navigate({
            search: { showSuccessDrawer: false },
            replace: true,
          });
        }
      }}
      open={Boolean(searchParams.showSuccessDrawer)}
      variant="secondary"
    >
      <MessageDrawerTitle>Telefone salvo com sucesso!</MessageDrawerTitle>
      <MessageDrawerBody>
        <div className="flex flex-col pt-4">
          <p>Esse número será informado aos pacientes que buscarem contato com você.</p>
        </div>
      </MessageDrawerBody>
      <MessageDrawerActions>
        <MessageDrawerActionButton onClick={() => router.history.back()}>
          Ok, entendi
        </MessageDrawerActionButton>
      </MessageDrawerActions>
    </MessageDrawerRoot>
  );
};
